
import React, { useEffect, useState, useCallback } from 'react';
import { authorsAPI, authAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import '../styles/adminCommonStyles.css';

export default function AdminAuthors() {
	const [authors, setAuthors] = useState([]);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState(null);
	const refetch = useCallback(async () => {
		setLoading(true);
		setFetchError(null);
		try {
			const res = await authorsAPI.getAuthors();
			// Backend may return either an array directly or an object { data: [...] }.
			const list = res.data?.data ?? res.data ?? [];
			setAuthors(list);
		} catch (err) {
			setFetchError(err.response?.data?.message || err.message || 'Failed to fetch authors');
		} finally {
			setLoading(false);
		}
	}, []);
	const [error, setError] = useState(null);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editForm, setEditForm] = useState({ id: '', name: '' });
	const [editError, setEditError] = useState(null);
	const [editLoading, setEditLoading] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [addForm, setAddForm] = useState({ name: '' });
	const [addError, setAddError] = useState(null);
	const [addLoading, setAddLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(null);
	const [successMessage, setSuccessMessage] = useState('');
	const navigate = useNavigate();

	useEffect(() => {
		setIsSuperAdmin(authAPI.hasRole('superadmin'));
		setIsAdmin(authAPI.hasRole('admin'));
	}, []);

	useEffect(() => {
		refetch();
	}, [refetch]);

	// Success message helper
	const showSuccess = (msg) => {
		setSuccessMessage(msg);
		setTimeout(() => setSuccessMessage(''), 3000);
	};

	// Add author handlers
	const handleAddInput = (e) => {
		setAddForm({ ...addForm, [e.target.name]: e.target.value });
	};
	const handleAddSubmit = async (e) => {
		e.preventDefault();
		setAddLoading(true);
		setAddError(null);
		try {
			const data = { name: addForm.name };
			const response = await authorsAPI.createAuthor(data);
			if (response.data?.success) {
				setShowAddModal(false);
				setAddForm({ name: '' });
				showSuccess('Author added successfully!');
				refetch();
			} else {
				setAddError(response.data?.message || 'Failed to add author');
			}
		} catch (err) {
			const errorMsg = err.response?.data?.message || err.message || 'Failed to add author';
			setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add author');
		} finally {
			setAddLoading(false);
		}
	};

	// Edit author handlers
	const handleEditClick = (author) => {
		setEditForm({ id: author.id, name: author.name });
		setShowEditModal(true);
		setEditError(null);
		// Scroll to edit form
		setTimeout(() => {
			scrollToForm();
		}, 0);
	};
	const handleEditInput = (e) => {
		setEditForm({ ...editForm, [e.target.name]: e.target.value });
	};
	const handleEditSubmit = async (e) => {
		e.preventDefault();
		setEditLoading(true);
		setEditError(null);
		try {
			const data = { name: editForm.name };
			const response = await authorsAPI.updateAuthor(editForm.id, data);
			if (response.data?.success) {
				setShowEditModal(false);
				showSuccess('Author updated successfully!');
				refetch();
			} else {
				setEditError(response.data?.message || 'Failed to update author');
			}
		} catch (err) {
			const errorMsg = err.response?.data?.message || err.message || 'Failed to update author';
			setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update author');
		} finally {
			setEditLoading(false);
		}
	};

	// Delete author
	const handleDeleteAuthor = async (authorId, authorName) => {
		if (!isSuperAdmin) {
			alert('Access denied. Only superadmin can delete authors.');
			return;
		}
		const confirmDelete = window.confirm(`Are you sure you want to delete "${authorName}"? This action cannot be undone.`);
		if (!confirmDelete) return;
		setDeleteLoading(authorId);
		setError(null);
		try {
			await authorsAPI.deleteAuthor(authorId);
			showSuccess('Author deleted successfully!');
			refetch();
		} catch (err) {
			const errorMsg = err.response?.data?.message || err.message || 'Failed to delete author';
			setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to delete author');
		} finally {
			setDeleteLoading(null);
		}
	};

	// Logout
	const handleLogout = () => {
		authAPI.logout();
		navigate('/admin/login');
	};

	// Permission logic
	const canEdit = isSuperAdmin || isAdmin;
	const canAdd = isSuperAdmin;
	const canDelete = isSuperAdmin;

	return (
	<div className="w-full max-w-screen-2xl mx-auto px-1 md:px-4 py-4 mt-2 landscape:px-8 landscape:py-8 flex flex-col items-center justify-center">
							<div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6 md:gap-0">
								<h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left drop-shadow-lg">Authors</h2>
								<div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
					<span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>{isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}</span>
					<button
						onClick={handleLogout}
						className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border border-red-300"
					>
						Logout
					</button>
				</div>
			</div>

			{successMessage && (
				<div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg">
					{successMessage}
				</div>
			)}

			{error && (
				<div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
					<strong>Error:</strong> {error}
				</div>
			)}

			{canAdd && (
				<button
					className="admin-btn-primary-lg mb-6"
					onClick={() => setShowAddModal(true)}
				>
					+ Add New Author
				</button>
			)}

			{showAddModal && (
				<div className="admin-modal-overlay">
					<div className="admin-modal-content admin-modal-form" id="add-author-form">
						<h3 className="text-xl font-bold mb-4">Add New Author</h3>
						<form onSubmit={handleAddSubmit}>
							<div className="mb-4">
								<label className="admin-label">Name</label>
								<input
									type="text"
									name="name"
									value={addForm.name}
									onChange={handleAddInput}
									className="admin-input"
									required
								/>
							</div>
							{addError && (
								<div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
									{addError}
								</div>
							)}
							<div className="flex justify-end gap-3">
								<button
									type="button"
									className="admin-btn-secondary"
									onClick={() => {
										setShowAddModal(false);
										setAddError(null);
									}}
									disabled={addLoading}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="admin-btn-primary"
									disabled={addLoading}
								>
									{addLoading ? 'Adding...' : 'Add Author'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showEditModal && (
				<div className="admin-modal-overlay-scroll">
					<div className="admin-modal-content admin-modal-form" id="edit-author-form">
						<h3 className="text-xl font-bold mb-4">Edit Author</h3>
						<form onSubmit={handleEditSubmit}>
							<div className="mb-4">
								<label className="admin-label">Name</label>
								<input
									type="text"
									name="name"
									value={editForm.name}
									onChange={handleEditInput}
									className="admin-input"
									required
								/>
							</div>
							{editError && (
								<div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
									{editError}
								</div>
							)}
							<div className="flex justify-end gap-3">
								<button
									type="button"
									className="admin-btn-secondary"
									onClick={() => {
										setShowEditModal(false);
										setEditError(null);
									}}
									disabled={editLoading}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="admin-btn-success"
									disabled={editLoading}
								>
									{editLoading ? 'Updating...' : 'Update Author'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{loading && (
				<LoadingSpinner message="Loading authors..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
			)}

			{!loading && (
			<div className="admin-table-container">
				<table className="admin-table">
					<thead className="admin-table-head">
						<tr>
							<th className="admin-table-header-cell">Name</th>
							<th className="admin-table-header-cell" style={{width: '120px'}}>Actions</th>
						</tr>
					</thead>
					<tbody className="admin-table-body-row">
							{authors.length === 0 ? (
								<tr>
									<td colSpan="2" className="py-8 px-4 text-center text-gray-500">
										<div className="text-lg">No authors found</div>
										<div className="text-sm mt-1">Add your first author to get started!</div>
									</td>
								</tr>
							) : (
								authors.map((author) => (
									<tr key={author.id} className="hover:bg-blue-50 transition-colors">
										<td className="py-4 px-4 font-medium text-gray-900">
											{author.name && author.name.length > 50 ? author.name.slice(0, 50) + '…' : author.name}
										</td>
										<td className="py-4 px-4" style={{width: '120px'}}>
											<div className="flex flex-col gap-2 items-stretch justify-center">
												{canEdit && (
													<button
														onClick={() => handleEditClick(author)}
														className="admin-btn-table-edit">
														Edit
													</button>
												)}
												{canDelete ? (
													<button
														onClick={() => handleDeleteAuthor(author.id, author.name)}
														disabled={deleteLoading === author.id}
														className="admin-btn-table-danger">
														{deleteLoading === author.id ? 'Deleting...' : 'Delete'}
													</button>
												) : (
													<button
														disabled
														className="admin-btn-table-disabled"
														title="Superadmin role required to delete authors">
														Delete
													</button>
												)}
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
